import React from 'react';
import { useI18next } from 'gatsby-plugin-react-i18next';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import * as urlHelper from '../../helpers/url.helper';
import {
  getListItem,
  getStructuredDataBreadcrumb,
} from '../../richSnippets/helpers';

export function getOrigin(location) {
  if (
    typeof location === 'undefined' ||
    typeof location?.origin === 'undefined'
  ) {
    return process.env.SITE_URL;
  }
  return location?.origin;
}

export function buildCanonicalLink(originalPathPage, location) {
  const { language, defaultLanguage } = useI18next();

  const origin = getOrigin(location);
  const pathWithOpeningAndTrailingSlash = originalPathPage
    .replace(/\/?$/, '/')
    .replace(/^\/?/, '/');
  const pathForLanguage = urlHelper.getPathForOtherLanguage(
    pathWithOpeningAndTrailingSlash,
    language,
  );
  let result = `${origin}${
    language === defaultLanguage ? '' : `/${language}`
  }${pathForLanguage}`;
  if (pathForLanguage.includes('.')) {
    result = result.replace(/^\/?/, '');
  }
  return result;
}

function getCanonicalTag(originalPathPage, location) {
  if (originalPathPage !== undefined) {
    const canonicalLink = buildCanonicalLink(originalPathPage, location);
    return <link rel="canonical" href={canonicalLink} />;
  }
  return null;
}

function getHrefLink(
  alternateLanguage,
  language,
  languages,
  defaultLanguage,
  location,
) {
  let newPath;
  const current_path = location?.pathname;
  const origin = getOrigin(location);

  if (alternateLanguage !== defaultLanguage) {
    if (language !== defaultLanguage) {
      newPath = current_path?.replace(
        `/${language}/`,
        `/${alternateLanguage}/`,
      );
    } else {
      newPath = `/${alternateLanguage}`.concat(current_path);
    }
  } else {
    newPath = current_path?.replace(`/${language}/`, '/');
  }

  return `${origin}${newPath}`;
}

function getAlternateTag({ hrefLang, href }) {
  return (
    <link
      rel="alternate"
      hrefLang={hrefLang}
      href={href}
      key={`${hrefLang}-${href}`}
    />
  );
}

const otherHrefLang = ['en', 'x-default'];

function getAlternateTags(
  translations,
  isGatsbyPage,
  location,
  originalPathPage,
) {
  const { languages, defaultLanguage, language } = useI18next();

  const origin = getOrigin(location);

  const homePathName = language === defaultLanguage ? '/' : `/${language}/`;
  let locationAdjusted;
  if (
    typeof location === 'undefined' &&
    typeof originalPathPage !== 'undefined'
  ) {
    const adjustedOriginalPathPage = () => {
      if (originalPathPage.endsWith('/') && originalPathPage.startsWith('/')) {
        return originalPathPage;
      }
      if (originalPathPage.endsWith('/') && !originalPathPage.startsWith('/')) {
        return `/${originalPathPage}`;
      }
      return `${originalPathPage}/`;
    };
    const otherPathName =
      language === defaultLanguage
        ? adjustedOriginalPathPage()
        : `/${language}${adjustedOriginalPathPage()}`;
    locationAdjusted =
      originalPathPage === ''
        ? { pathname: homePathName }
        : { pathname: otherPathName };
  } else if (
    typeof location === 'undefined' &&
    typeof originalPathPage === 'undefined'
  ) {
    locationAdjusted = { pathname: homePathName };
  } else {
    locationAdjusted = location;
  }

  // posts: translations is an empty array
  // wp pages: translations should always have an array with length > 0

  const hasEmptyTranslations =
    (translations &&
      Array.isArray(translations) &&
      translations.length === 0) ||
    false;
  const hasFilledTranslations =
    (translations && Array.isArray(translations) && translations.length > 0) ||
    false;

  const result =
    (isGatsbyPage &&
      languages.map((alternateLanguage) => {
        const pathWithoutLanguage = locationAdjusted.pathname.replace(
          /^(\/(es|en)\b\/?)/,
          '/',
        );
        const languageLocationPath = `${urlHelper.getPathForOtherLanguage(pathWithoutLanguage, alternateLanguage)}`;
        const languageLocation = {
          ...locationAdjusted,
          pathname:
            language === 'en'
              ? `/en${languageLocationPath}`
              : languageLocationPath,
        };
        if (alternateLanguage === defaultLanguage) {
          return getAlternateTag({
            hrefLang: alternateLanguage,
            href: getHrefLink(
              alternateLanguage,
              language,
              languages,
              defaultLanguage,
              languageLocation,
            ),
          });
        } else {
          return otherHrefLang.map((itemLanguage) =>
            getAlternateTag({
              hrefLang: itemLanguage,
              href: getHrefLink(
                alternateLanguage,
                language,
                languages,
                defaultLanguage,
                languageLocation,
              ),
            }),
          );
        }
      })) ||
    [];

  // cover WP pages generated by the template tld-page
  const resultFilledTranslations =
    (hasFilledTranslations &&
      languages.map((alternateLanguage) => {
        const otherLanguageUri = translations.filter(
          (translation) => translation.language.slug === alternateLanguage,
        )?.[0]?.uri;

        const sameLanguageUri = getHrefLink(
          alternateLanguage,
          language,
          languages,
          defaultLanguage,
          locationAdjusted,
        );

        const href =
          language !== alternateLanguage
            ? `${origin}${otherLanguageUri}`
            : sameLanguageUri;

        // TODO: understand why this code fails with the error "tag[primaryAttributeKey].toLowerCase is not a function"
        //  return getAlternateTag({
        //   hrefLang: `${alternateLanguage}-${defaultLanguage}`,
        //   href: { href },
        // });
        if (alternateLanguage === defaultLanguage) {
          return (
            <link
              key={href}
              rel="alternate"
              hrefLang={alternateLanguage}
              href={href}
            />
          );
        } else {
          return otherHrefLang.map((itemLanguage) => (
            <link
              key={href}
              rel="alternate"
              hrefLang={itemLanguage}
              href={href}
            />
          ));
        }
      })) ||
    [];

  return [...result, ...resultFilledTranslations];
}

function getRichSnippets(richSnippets) {
  if (Array.isArray(richSnippets) && richSnippets.length > 0) {
    return richSnippets.map((snippet) => {
      if (snippet) {
        const key =
          Array.isArray(snippet) && snippet.length > 0
            ? snippet[0]['@type']
            : snippet['@type'];
        return (
          <script type="application/ld+json" key={key}>
            {JSON.stringify(snippet)}
          </script>
        );
      }
      return null;
    });
  }
  return null;
}

const HeadHelmet = ({
  originalPathPage,
  enableAlternateTags,
  externalDescription,
  isGatsbyPage,
  richSnippets,
  title,
  translation,
  translations,
  location,
}) => {
  const { t } = useTranslation([translation, 'richSnippets']);

  const description = externalDescription || t('head.meta.description');
  const { language, defaultLanguage, path, originalPath, siteUrl } =
    useI18next();
  const titleWithSuffix = `${title || t('head.title')}`;
  const canonicalTag = getCanonicalTag(originalPathPage, location);
  const alternateTags = getAlternateTags(
    translations,
    isGatsbyPage,
    location,
    originalPathPage,
  );

  const environment = process.env.GATSBY_ACTIVE_ENV || 'development'; // Default to 'development'

  const isReportPage = path.includes('reports');

  const metaContent =
    environment !== 'production' || isReportPage
      ? 'noindex, nofollow'
      : 'index, follow';

  const homeUri = language === defaultLanguage ? '/' : `/${language}/`;

  const homeListItem = getListItem({
    position: 0,
    pageTitle: t('breadcrumbs.home', { ns: 'richSnippets' }),
    uri: homeUri,
  });
  const pageListItem = getListItem({
    position: 1,
    pageTitle: t('head.title'),
    uri: path,
  });

  const gatsbyBreadcrumbs =
    originalPath === '/' ? [homeListItem] : [homeListItem, pageListItem];

  const isGatsbyPageFirstLevel =
    isGatsbyPage && originalPath.split('/').length <= 3;
  const structuredDataBreadcrumbs =
    (isGatsbyPageFirstLevel &&
      getStructuredDataBreadcrumb(gatsbyBreadcrumbs)) ||
    [];

  // The gatsby-plugin-react-helmet package will be deprecated in the future.
  // https://www.gatsbyjs.com/plugins/gatsby-plugin-react-helmet/#%EF%B8%8F-this-package-will-be-deprecated
  const structuredData =
    Array.isArray(richSnippets) && richSnippets.length > 0
      ? [...richSnippets, ...structuredDataBreadcrumbs]
      : structuredDataBreadcrumbs;
  return (
    <Helmet
      htmlAttributes={{
        lang: language,
      }}
    >
      <title>{titleWithSuffix}</title>
      {canonicalTag}
      {enableAlternateTags && alternateTags}
      <meta name="description" content={description} />
      <meta name="robots" content={metaContent} />
      <link rel="icon" href="/icon-32x32.ico" type="image/ico" />
      <meta property="og:title" content={titleWithSuffix} />
      <meta property="og:description" content={description} />
      <meta
        property="og:image"
        content={`${siteUrl}/images/sunhero-social-sharing.webp`}
      />
      {getRichSnippets(structuredData)}
      <script defer>
        {`
          if(!sessionStorage.getItem('referrer')) {
            sessionStorage.setItem('referrer', document?.referrer);
          }
          if(!sessionStorage.getItem('search')) {
            sessionStorage.setItem('search', location?.search);
          }
        `}
      </script>
    </Helmet>
  );
};

export default HeadHelmet;
